import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, Menu, Moon, Search, Sun, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { loadSearchIndex, rankResults, type SearchIndexEntry } from '../utils/toolSearchUtils';
import { getMostUsedTools, getRecentTools, TOOL_USAGE_UPDATED_EVENT } from '../utils/toolUsageUtils';
import { FAVORITES_UPDATED_EVENT, getFavorites } from '../utils/favoritesUtils';
import { TOOL_COUNT } from '../data/siteMeta';
import { InstallButton } from './InstallButton';
import './SiteHeader.css';

interface SiteHeaderProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  promptReady: boolean;
  onInstalled: () => void;
}

// Shown when nothing was typed and there is no history yet.
const POPULAR_IDS = ['json-parser', 'jwt-decoder', 'hash-generator', 'qr-code-generator', 'password-generator'];

// Resolves stored tool IDs (from toolUsageUtils) back to real entries via
// the loaded index, dropping any ID that no longer matches a real tool
// (e.g. a since-removed one) and any duplicate that appears in both lists.
function resolveIds(ids: string[], index: SearchIndexEntry[], exclude: Set<string>): SearchIndexEntry[] {
  const byId = new Map(index.map((entry) => [entry.id, entry]));
  const resolved: SearchIndexEntry[] = [];
  for (const id of ids) {
    if (exclude.has(id)) continue;
    const entry = byId.get(id);
    if (entry) {
      resolved.push(entry);
      exclude.add(id);
    }
  }
  return resolved;
}

interface Group {
  label: string;
  entries: SearchIndexEntry[];
}

const SiteHeader = ({ sidebarOpen, onToggleSidebar, promptReady, onInstalled }: SiteHeaderProps) => {
  const { theme, toggleTheme } = useTheme();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState<SearchIndexEntry[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [usageRevision, setUsageRevision] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchResults = useMemo(() => {
    if (!index || !query.trim()) return [];
    return rankResults(query, index);
  }, [index, query]);

  // Groups shown for each state: typed text -> Tools; empty -> Recent, then Popular (when there is no history).
  const groups: Group[] = useMemo(() => {
    if (query.trim()) return searchResults.length ? [{ label: 'Tools', entries: searchResults }] : [];
    if (!index) return [];
    const seen = new Set<string>();
    const favorites = resolveIds(getFavorites(), index, seen).slice(0, 8);
    const recent = resolveIds(getRecentTools(), index, seen).slice(0, 8);
    const used = resolveIds(getMostUsedTools(), index, seen).slice(0, 5);
    const out: Group[] = [];
    if (favorites.length) out.push({ label: 'Favorites', entries: favorites });
    if (recent.length) out.push({ label: 'Recent', entries: recent });
    if (used.length) out.push({ label: 'Most used', entries: used });
    if (!out.length) out.push({ label: 'Popular', entries: resolveIds(POPULAR_IDS, index, new Set()) });
    return out;
    // usageRevision re-reads the stored history when a tool is visited.
  }, [index, query, searchResults, usageRevision]);

  useEffect(() => {
    const refreshUsage = () => setUsageRevision((revision) => revision + 1);
    window.addEventListener(TOOL_USAGE_UPDATED_EVENT, refreshUsage);
    window.addEventListener(FAVORITES_UPDATED_EVENT, refreshUsage);
    return () => {
      window.removeEventListener(TOOL_USAGE_UPDATED_EVENT, refreshUsage);
      window.removeEventListener(FAVORITES_UPDATED_EVENT, refreshUsage);
    };
  }, []);

  // The flat, keyboard-navigable list across all groups.
  const activeList = useMemo(() => groups.flatMap((g) => g.entries), [groups]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query, isOpen]);

  const openPanel = () => {
    setIsOpen(true);
    if (!index) {
      loadSearchIndex()
        .then(setIndex)
        .catch(() => {
          // Leave index null: the panel shows "Loading…" rather than breaking the header.
        });
    }
  };

  const closePanel = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const goToEntry = (entry: SearchIndexEntry) => {
    closePanel();
    setQuery('');
    inputRef.current?.blur();
    navigate(entry.path);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (activeList.length === 0) return;
      setActiveIndex((i) => (i + 1) % activeList.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (activeList.length === 0) return;
      setActiveIndex((i) => (i <= 0 ? activeList.length - 1 : i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const entry = activeIndex >= 0 ? activeList[activeIndex] : activeList[0];
      if (entry) goToEntry(entry);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closePanel();
      inputRef.current?.blur();
    }
  };

  // Ctrl+K / Cmd+K and "/" (ignored while already typing anywhere else on
  // the page) always focus this one search, from any route.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isShortcut = (event.key === 'k' || event.key === 'K') && (event.ctrlKey || event.metaKey);
      if (isShortcut) {
        event.preventDefault();
        inputRef.current?.focus();
        return;
      }
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement;
      if (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Closes the panel on any click outside the whole search widget (the
  // standard accessible-combobox "click away" behaviour).
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  // On phones the open search is a full-screen panel: stop the page behind it from scrolling.
  useEffect(() => {
    document.body.classList.toggle('dsk-search-open', isOpen);
    return () => document.body.classList.remove('dsk-search-open');
  }, [isOpen]);

  const typed = query.trim().length > 0;
  const activeId = activeIndex >= 0 && activeList[activeIndex] ? `site-header-option-${activeList[activeIndex].id}` : undefined;

  return (
    <header className="site-header dsk-root">
      <button
        className="site-header-menu-btn"
        onClick={onToggleSidebar}
        aria-label={sidebarOpen ? 'Close tool navigation' : 'Open tool navigation'}
        aria-expanded={sidebarOpen}
      >
        <Menu size={20} />
      </button>

      <Link to="/" className="site-header-brand" title="DevStackKit — Home">
        <span className="site-header-mark" aria-hidden="true">{'</>'}</span>
        <span>DevStackKit</span>
      </Link>

      {/* Phone: this icon opens the full-screen search; hidden on wider screens. */}
      <button type="button" className="site-header-searchbtn" aria-label="Search tools" onClick={() => { openPanel(); window.setTimeout(() => inputRef.current?.focus(), 0); }}>
        <Search size={18} aria-hidden="true" />
      </button>

      <div className={`site-header-search${isOpen ? ' is-open' : ''}`} ref={containerRef}>
        <Search size={16} className="site-header-search-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? 'site-header-listbox' : undefined}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-label="Search tools"
          value={query}
          placeholder={`Search ${index ? index.length : TOOL_COUNT} tools...`}
          onFocus={openPanel}
          onChange={(event) => {
            setQuery(event.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <kbd className="site-header-shortcut-hint" aria-hidden="true">Ctrl K</kbd>
        <button type="button" className="site-header-searchclose" aria-label="Close search" onClick={() => { closePanel(); setQuery(''); }}>
          <X size={20} aria-hidden="true" />
        </button>

        {isOpen && (
          <div className="site-header-panel">
            {groups.length > 0 ? (
              <div className="site-header-listbox" role="listbox" id="site-header-listbox" aria-label={typed ? 'Search results' : 'Suggested tools'}>
                {groups.map((group) => (
                  <div role="group" aria-label={group.label} key={group.label}>
                    <p className="site-header-group-label">{group.label}</p>
                    {group.entries.map((entry) => {
                      const i = activeList.indexOf(entry);
                      return (
                        <div
                          key={entry.id}
                          id={`site-header-option-${entry.id}`}
                          role="option"
                          aria-selected={i === activeIndex}
                          className={`site-header-option${i === activeIndex ? ' active' : ''}`}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => goToEntry(entry)}
                          onMouseEnter={() => setActiveIndex(i)}
                        >
                          <span className="site-header-option__tile" aria-hidden="true">{entry.title.charAt(0)}</span>
                          <span className="site-header-option__text">
                            <span className="site-header-option__title">{entry.title}</span>
                            {entry.category ? <span className="site-header-option__meta">{entry.category}</span> : null}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <p className="site-header-empty" role="status">
                {!index ? 'Loading…' : typed ? <>No tools match “{query.trim()}”.</> : 'Start typing to search tools.'}
              </p>
            )}
            <p className="site-header-suggest">
              <Link to="/contact" onClick={closePanel}>No match? Suggest a tool →</Link>
            </p>
          </div>
        )}
      </div>

      <div className="site-header-actions">
        <InstallButton promptReady={promptReady} onInstalled={onInstalled} />
        <Link to="/tools" className="site-header-link" title="All tools">
          <LayoutGrid size={16} aria-hidden="true" />
          <span>All tools</span>
        </Link>
        <button
          type="button"
          className="site-header-theme"
          role="switch"
          aria-checked={theme === 'dark'}
          data-theme-state={theme}
          aria-label="Dark mode"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
        >
          <span className="site-header-theme__knob" aria-hidden="true">
            {theme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
          </span>
        </button>
      </div>
    </header>
  );
};

export default SiteHeader;
